import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
  IconButton,
  useTheme,
  useMediaQuery,
  Chip,
  Stack,
  Link,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import NavigationSidebar from "../NavigationSidebar";
import InfoIcon from "@mui/icons-material/Info";
import ReactMarkdown from "react-markdown";
import ChatTemplates from "./ChatTemplates";
import MenuIcon from "@mui/icons-material/Menu";
import Marquee from "react-fast-marquee";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SimilarQuestions from "./SimilarQuestions";
import KeywordHighlighter from "./KeywordHighlighter";
import TextGenerateEffect from "./TextGenerateEffect";

// Updated color scheme based on Figma design
const themeColors = {
  primary: "#45B5B9",
  secondary: "#DF7468",
  background: "#FFFFFF",
  text: "#41444B",
  cardColors: [
    "#FDE36F", // Yellow
    "#F2B994", // Orange
    "#E89FB2", // Pink
    "#64CBCC", // Turquoise
    "#487C9E", // Blue
  ],
  sideNav: "#30313E",
  hyperlink: "#DF7468", // Red hyperlink color
};

// Question card styles based on Figma design
const questionCardStyles = {
  width: 157,
  height: 157,
  padding: "15px",
  borderRadius: "5px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  cursor: "pointer",
  transition: "transform 0.2s ease",
  "&:hover": {
    transform: "scale(1.05)",
  },
};

// Mobile question card styles
const mobileQuestionCardStyles = {
  ...questionCardStyles,
  width: 120,
  height: 120,
  padding: "10px",
};

const gradients = [
  "linear-gradient(45deg, rgb(58, 59, 60), rgb(74, 77, 79))", // Darker blue gradient
  "linear-gradient(45deg, rgb(58, 59, 60), rgb(74, 77, 79))", // Darker green gradient
  "linear-gradient(45deg, rgb(58, 59, 60), rgb(74, 77, 79))", // Darker purple gradient
  "linear-gradient(45deg, rgb(58, 59, 60), rgb(74, 77, 79))", // Darker orange gradient
];

const ThinkingAnimation = () => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 2,
        bgcolor: "background.paper",
        borderRadius: "20px 20px 20px 0",
        maxWidth: "fit-content",
        boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
      }}
    >
      <Typography
        sx={{
          fontFamily: "Poppins",
          fontWeight: 500,
          color: themeColors.text, // Using existing color from theme
          fontSize: "16px",
        }}
      >
        Caroline is thinking
      </Typography>
      <Box
        sx={{
          display: "flex",
          gap: 0.5,
          alignItems: "center",
          "& .dot": {
            width: 6,
            height: 6,
            backgroundColor: themeColors.primary,
            borderRadius: "50%",
            animation: "bounce 1.4s infinite ease-in-out both",
            "&:nth-of-type(1)": {
              animationDelay: "-0.32s",
            },
            "&:nth-of-type(2)": {
              animationDelay: "-0.16s",
            },
          },
          "@keyframes bounce": {
            "0%, 80%, 100%": {
              transform: "scale(0)",
            },
            "40%": {
              transform: "scale(1)",
            },
          },
        }}
      >
        <span className="dot" />
        <span className="dot" />
        <span className="dot" />
      </Box>
    </Box>
  );
};

const TypingMessage = ({ text, onComplete }) => {
  const [displayText, setDisplayText] = useState("");

  useEffect(() => {
    let currentIndex = 0;
    const interval = setInterval(() => {
      if (currentIndex <= text.length) {
        setDisplayText(text.slice(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(interval);
        if (onComplete) onComplete();
      }
    }, 20); // Adjust speed here (lower = faster)

    return () => clearInterval(interval);
  }, [text, onComplete]);

  return <Typography>{displayText}</Typography>;
};

// Move templates to a shared location at the top of the file
const templates = [
  {
    title: "Tell me more about my Career Report",
    prompt: "Tell me more about my Career Report",
  },
  {
    title: "What Career Animal am I?",
    prompt: "Tell more about my career animal",
  },
  {
    title: "What's my career direction?",
    prompt: "What's my career direction?",
  },
  {
    title: "How can I improve my career health?",
    prompt: "Give me some ideas on how can I improve my career health?",
  },
  {
    title: "What could make me happier at work?",
    prompt: "What could make me happier at work? (what are my dissatisfiers)",
  },
  {
    title: "How do I progress my career?",
    prompt: "Give me suggestion on how do I progress my career?",
  },
  {
    title: "What skills should I enhance?",
    prompt: "What skills should I enhance?",
  },
];

const AIChatPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [input, setInput] = useState("");
  const [currentChat, setCurrentChat] = useState([]);
  const [chatHistory, setChatHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(!isMobile);
  const messagesEndRef = useRef(null);
  const resizeTimeoutRef = useRef(null);
  const [followUpQuestions, setFollowUpQuestions] = useState([]);
  const [sourceDocuments, setSourceDocuments] = useState([]);
  const [username, setUsername] = useState("");
  const [isTemplatesOpen, setIsTemplatesOpen] = useState(false);
  const [hasShownMarquee, setHasShownMarquee] = useState(false);
  const [animatingMessageIndex, setAnimatingMessageIndex] = useState(null);
  const [completedAnimations, setCompletedAnimations] = useState(new Set());
  const [messageDisplayComplete, setMessageDisplayComplete] = useState({});
  const [localTemplates, setLocalTemplates] = useState(templates);
  const [checkingAnswers, setCheckingAnswers] = useState(true);

  useEffect(() => {
    // Check if user has submitted answers
    const checkUserAnswers = async () => {
      setCheckingAnswers(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/user/${userId}/has-answers`
        );
        const data = await response.json();
        if (!data.hasAnswers) {
          navigate(`/questions`, { replace: true, state: { userId } });
        }
      } catch (error) {
        navigate(`/questions`, { replace: true, state: { userId } });
      } finally {
        setCheckingAnswers(false);
      }
    };
    if (userId) checkUserAnswers();
  }, [userId, navigate]);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/user/${userId}/profile-data`
        );
        const data = await response.json();

        if (data.success && data.profile) {
          setUsername(data.profile.name);
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
      }
    };

    fetchUserProfile();
  }, [userId]);

  useEffect(() => {
    setShowHistory(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, []);

  const scrollToBottom = () => {
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }
    resizeTimeoutRef.current = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  useEffect(() => {
    scrollToBottom();
  }, [currentChat]);

  const sendMessage = async (message) => {
    try {
      setIsLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/ai/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId,
          question: message,
        }),
      });

      const data = await response.json();

      if (!data.success) {
        setCurrentChat((prev) => [
          ...prev,
          { role: "user", content: message },
          {
            role: "assistant",
            content: data.message || "An error occurred. Please try again.",
            isError: true,
          },
        ]);
        return;
      }

      const newMessageIndex = currentChat.length + 1;
      setCurrentChat((prev) => [
        ...prev,
        { role: "user", content: message },
        {
          role: "assistant",
          content: data.message,
          sources: data.sourceDocuments,
          keywords: data.keywords,
        },
      ]);
      setAnimatingMessageIndex(newMessageIndex);

      setFollowUpQuestions(data.followUpQuestions || []);
      setInput("");
    } catch (error) {
      console.error("Error sending message:", error);
      setCurrentChat((prev) => [
        ...prev,
        { role: "user", content: message },
        {
          role: "assistant",
          content: "I apologize, but I encountered an error. Please try again.",
          isError: true,
        },
      ]);
    } finally {
      setIsLoading(false);
      setHasShownMarquee(true);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (input.trim()) {
        sendMessage(input);
      }
    }
  };

  const FollowUpQuestions = ({ questions }) => {
    if (!questions || questions.length === 0) return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            ml: 1,
            mb: 1,
            display: "block",
            fontFamily: "Poppins",
            fontWeight: 700,
            fontSize: 21,
          }}
        >
          Suggested questions:
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, 1fr)",
              sm: "repeat(3, 1fr)",
              md: "repeat(4, 1fr)",
            },
            gap: 2,
            mt: 1,
          }}
        >
          {questions.map((question, index) => (
            <Paper
              key={index}
              onClick={() => {
                sendMessage(question);
                setFollowUpQuestions([]); // Clear follow-ups when one is selected
              }}
              sx={{
                bgcolor:
                  themeColors.cardColors[index % themeColors.cardColors.length],
                p: 2,
                borderRadius: "5px",
                cursor: "pointer",
                transition: "transform 0.2s ease",
                "&:hover": {
                  transform: "scale(1.05)",
                },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 100,
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Poppins",
                  fontWeight: 700,
                  fontSize: { xs: 16, sm: 16 },
                  color: themeColors.text,
                  textAlign: "center",
                  lineHeight: 1.4,
                }}
              >
                {question}
              </Typography>
            </Paper>
          ))}
        </Box>
      </Box>
    );
  };

  const SourceDocuments = ({ sources }) => {
    if (!sources || sources.length === 0) return null;

    return (
      <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
        <Tooltip title="Sources used for this response">
          <InfoIcon sx={{ fontSize: 16, color: "text.secondary" }} />
        </Tooltip>
        <Typography variant="caption" color="text.secondary">
          Sources:{" "}
          {sources.map((source, index) => (
            <span key={`source-${source.id || index}`}>
              {index > 0 && <span key={`separator-${index}`}>, </span>}
              <Tooltip
                title={`Relevance: ${Math.round(source.relevance * 100)}%`}
              >
                <Link
                  component="span"
                  sx={{
                    cursor: "pointer",
                    color: themeColors.hyperlink,
                    textDecoration: "underline",
                    "&:hover": {
                      opacity: 0.8,
                    },
                  }}
                  onClick={() => {
                    /* Handle document click */
                  }}
                >
                  {source.title}
                </Link>
              </Tooltip>
            </span>
          ))}
        </Typography>
      </Box>
    );
  };

  const handleTemplateSelect = (templateText) => {
    setInput(templateText);
    sendMessage(templateText);
    setHasShownMarquee(true);
  };

  // Remove or modify this effect to prevent templates sidebar from showing on load
  useEffect(() => {
    // Only update template visibility when user manually toggles
    if (!location.pathname.includes("/ai-chat")) {
      setIsTemplatesOpen(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/ai/templates`
        );
        const data = await response.json();
        // If no templates from API, use default templates from ChatTemplates
        if (!data || data.length === 0) {
          setLocalTemplates([
            {
              title: "Tell me more about my Career Report",
              prompt: "Tell me more about my Career Report",
            },
            {
              title: "What Career Animal am I?",
              prompt: "Tell more about my career animal",
            },
            {
              title: "What's my career direction?",
              prompt: "What's my career direction?",
            },
            {
              title: "How can I improve my career health?",
              prompt:
                "Give me some ideas on how can I improve my career health?",
            },
            {
              title:
                "What could make me happier at work? (what are my dissatisfiers)",
              prompt:
                "What could make me happier at work? (what are my dissatisfiers)",
            },
            {
              title: "How do I progress my career?",
              prompt: "Give me suggestion on how do I progress my career?",
            },
            {
              title: "What skills should I enhance?",
              prompt: "What skills should I enhance?",
            },
          ]);
        } else {
          setLocalTemplates(data);
        }
      } catch (error) {
        console.error("Error fetching templates:", error);
        // Set default templates on error
        setLocalTemplates(templates);
      }
    };

    fetchTemplates();
  }, []);

  const handleSelectSimilarQuestion = (question) => {
    setInput(question);
  };

  if (checkingAnswers) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      <NavigationSidebar userId={userId} />

      {/* Main Chat Area */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          bgcolor: "background.paper",
          position: "relative",
          overflow: "hidden",
          transition: "margin 0.3s ease",
          marginRight: isTemplatesOpen ? "300px" : 0,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontFamily: "Poppins",
              fontWeight: 700,
              fontSize: 32,
              color: themeColors.primary,
            }}
          >
            Ask CoachCAROL
          </Typography>
          <IconButton
            onClick={() => setIsTemplatesOpen(true)}
            sx={{
              bgcolor: themeColors.secondary,
              color: "white",
              "&:hover": {
                bgcolor: themeColors.secondary,
                opacity: 0.9,
              },
              display: isTemplatesOpen ? "none" : "flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 0.5,
              borderRadius: "5px",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Merriweather",
                fontWeight: 700,
                fontSize: 21,
                textTransform: "uppercase",
              }}
            >
              Discover
            </Typography>
          </IconButton>
        </Box>

        {/* Messages Container */}
        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            p: 3,
            margin: "0 auto",
            width: "100%",
            maxWidth: "800px",
            "&::-webkit-scrollbar": {
              display: "none",
            },
            msOverflowStyle: "none", // IE and Edge
            scrollbarWidth: "none", // Firefox
          }}
        >
          {currentChat.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                justifyContent: "space-between",
              }}
            >
              {/* Welcome Message */}
              <Typography
                variant="h4"
                sx={{
                  fontFamily: "Merriweather",
                  fontWeight: 700,
                  fontSize: 32,
                  color: themeColors.text,
                  mb: 3,
                }}
              >
                What can I help with?
              </Typography>

              {/* Question Cards Grid */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, 1fr)",
                    sm: "repeat(3, 1fr)",
                    md: "repeat(4, 1fr)",
                  },
                  gap: {
                    xs: "16px",
                    sm: "24px",
                    md: "24px",
                  },
                  mb: 4,
                  justifyContent: "center",
                  alignContent: "flex-end",
                  pt: { xs: 2, sm: 3, md: 4 },
                  width: "100%",
                  mx: "auto",
                }}
              >
                {localTemplates.map((template, index) => (
                  <Paper
                    key={index}
                    onClick={() => handleTemplateSelect(template.prompt)}
                    sx={{
                      ...(isMobile
                        ? mobileQuestionCardStyles
                        : questionCardStyles),
                      bgcolor:
                        themeColors.cardColors[
                          index % themeColors.cardColors.length
                        ],
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Poppins",
                        fontWeight: 700,
                        fontSize: { xs: 18, sm: 18 },
                        lineHeight: 1.4,
                        color: themeColors.text,
                        textAlign: "center",
                      }}
                    >
                      {template.title}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            </Box>
          ) : (
            /* Chat Messages */
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {currentChat.map((message, index) => (
                <Box
                  key={index}
                  sx={{
                    alignSelf:
                      message.role === "user" ? "flex-end" : "flex-start",
                    maxWidth: "85%",
                  }}
                >
                  {message.role === "user" ? (
                    <Paper
                      elevation={1}
                      sx={{
                        p: 2,
                        bgcolor: themeColors.cardColors[3],
                        color: themeColors.text,
                        borderRadius: "20px 20px 0 20px",
                        wordBreak: "break-word",
                        minWidth: "200px",
                        "& a": {
                          color: themeColors.hyperlink,
                          textDecoration: "underline",
                          "&:hover": {
                            opacity: 0.8,
                          },
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "Poppins",
                          fontWeight: 500,
                          fontSize: 18,
                          lineHeight: 1.5,
                        }}
                      >
                        {message.content}
                      </Typography>
                    </Paper>
                  ) : (
                    <Box>
                      <Paper
                        elevation={1}
                        sx={{
                          p: 2,
                          bgcolor: "background.paper",
                          borderRadius: "20px 20px 20px 0",
                          wordBreak: "break-word",
                          boxShadow: "none",
                          minWidth: "250px",
                          "& a": {
                            color: themeColors.hyperlink,
                            textDecoration: "underline",
                            "&:hover": {
                              opacity: 0.8,
                            },
                          },
                        }}
                      >
                        {!completedAnimations.has(index) &&
                        index === animatingMessageIndex ? (
                          <Box>
                            {message.title && (
                              <Typography
                                sx={{
                                  fontFamily: "Merriweather",
                                  fontWeight: 400,
                                  fontSize: 26,
                                  lineHeight: 1.25,
                                  color: themeColors.text,
                                  mb: 2,
                                }}
                              >
                                {message.title}
                              </Typography>
                            )}
                            <TextGenerateEffect
                              text={message.content}
                              onComplete={() => {
                                setCompletedAnimations(
                                  (prev) => new Set([...prev, index])
                                );
                                setAnimatingMessageIndex(null);
                                setMessageDisplayComplete((prev) => ({
                                  ...prev,
                                  [index]: true,
                                }));
                              }}
                            />
                          </Box>
                        ) : (
                          <>
                            <Typography
                              sx={{
                                color: themeColors.text,
                                fontFamily: "Poppins",
                                fontWeight: 500,
                                fontSize: 19,
                                lineHeight: 1.71,
                              }}
                            >
                              {message.isError ? (
                                <span style={{ color: "red" }}>
                                  {message.content}
                                </span>
                              ) : (
                                <KeywordHighlighter
                                  content={message.content}
                                  keywords={message.keywords || []}
                                  onKeywordClick={sendMessage}
                                  highlightColor={themeColors.hyperlink}
                                />
                              )}
                            </Typography>
                          </>
                        )}
                      </Paper>
                      <SourceDocuments sources={message.sources} />
                      {index === currentChat.length - 1 &&
                        !isLoading &&
                        (completedAnimations.has(index) ||
                          messageDisplayComplete[index]) && (
                          <FollowUpQuestions questions={followUpQuestions} />
                        )}
                    </Box>
                  )}
                </Box>
              ))}
              {isLoading && <ThinkingAnimation />}
              <div ref={messagesEndRef} />
            </Box>
          )}
        </Box>

        {/* Input Area */}
        <Box
          sx={{
            p: 3,
            bgcolor: "background.paper",
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              position: "relative",
              bgcolor: "#FDE36F",
              borderRadius: "5px",
              p: 0.2,
              maxWidth: "800px",
              margin: "0 auto",
            }}
          >
            <TextField
              fullWidth
              multiline
              maxRows={4}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask anything"
              disabled={isLoading}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "background.paper",
                  borderRadius: "5px",
                  "& fieldset": {
                    borderColor: "transparent",
                  },
                  "&:hover fieldset": {
                    borderColor: "transparent",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "transparent",
                  },
                },
                "& .MuiInputBase-input": {
                  fontFamily: "Poppins",
                  fontWeight: 500,
                  fontSize: 18,
                },
              }}
              InputProps={{
                endAdornment: (
                  <Box
                    sx={{
                      bgcolor: themeColors.secondary,
                      borderRadius: "50%",
                      width: 32,
                      height: 32,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 1,
                    }}
                  >
                    <IconButton
                      onClick={() => input.trim() && sendMessage(input)}
                      disabled={isLoading || !input.trim()}
                      sx={{
                        color: "white",
                        p: 0.5,
                        "&.Mui-disabled": {
                          color: "white",
                          opacity: 0.7,
                        },
                      }}
                    >
                      {isLoading ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <SendIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Box>
                ),
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Discover Sidebar */}
      {isTemplatesOpen && (
        <Paper
          sx={{
            width: 300,
            height: "100%",
            position: "fixed",
            right: 0,
            top: 0,
            zIndex: theme.zIndex.drawer - 1,
            flexShrink: 0,
            borderLeft: 1,
            borderColor: "divider",
            display: "flex",
            flexDirection: "column",
            bgcolor: "background.paper",
          }}
        >
          <ChatTemplates
            templates={localTemplates}
            onTemplateSelect={handleTemplateSelect}
            isOpen={isTemplatesOpen}
            onToggle={() => setIsTemplatesOpen(!isTemplatesOpen)}
          />
        </Paper>
      )}
    </Box>
  );
};

export default AIChatPage;
